import { IMG } from "@/lib/site";

/*
 * CONCEPT SHOWCASE — "The Aurelia"
 * A fictional demonstration project created by Sthaan to show developers
 * what a bespoke Sthaan project page can be. Every fact here is
 * illustrative. Real project modules follow the same flexible structure:
 * declare only the sections for which the developer supplied material.
 */
const aurelia = {
    slug: "the-aurelia",
    visibility: "private",
    demo: true,
    name: "The Aurelia",
    tagline: "Sky Villas & Residences",
    category: "residential",
    city: "Mumbai",
    citySlug: "mumbai",
    locality: "Worli Sea Face",
    status: "Concept Showcase",
    priceLabel: null,
    whatsapp: "919426810580",
    coverImage: IMG.hero_dusk,
    hero: {
        image: IMG.hero_dusk,
        overline: "A Sthaan Concept Showcase · Worli Sea Face, Mumbai",
        lines: ["The Aurelia", "Sky Villas & Residences"],
        sub: "A fictional landmark residence, composed to demonstrate how Sthaan turns a developer's material into a cinematic digital showcase.",
    },
    sections: [
        {
            type: "overview",
            overline: "Overview",
            title: "An address composed around light, air and the Arabian Sea.",
            paragraphs: [
                "The Aurelia imagines eighty-eight sky residences arranged across three slender towers on the Worli seafront — each home oriented to frame the horizon, each floor plate limited to two residences for complete privacy.",
                "This page is a concept showcase: it demonstrates the pacing, depth and polish Sthaan brings to a real project's photographs, plans, films and documents. Every figure below is illustrative.",
            ],
            stats: [
                { value: "3", label: "Towers" },
                { value: "88", label: "Sky Residences" },
                { value: "4.2", label: "Acres (illustrative)" },
                { value: "2", label: "Residences per floor" },
            ],
        },
        {
            type: "highlights",
            overline: "Highlights",
            title: "Composed details, not checklist features.",
            items: [
                {
                    title: "Sea-facing sky decks",
                    text: "Every residence opens onto a deep, west-facing deck — conceived as an outdoor room rather than a balcony.",
                },
                {
                    title: "Private lift lobbies",
                    text: "Dedicated elevators arrive directly into each residence's own vestibule.",
                },
                {
                    title: "Double-height arrival court",
                    text: "A landscaped, covered court separates the bustle of the city from the calm of the towers.",
                },
                {
                    title: "Club Aurelia",
                    text: "A residents-only club spanning wellness, dining, work and celebration across two curated levels.",
                },
                {
                    title: "Two residences per floor",
                    text: "Low-density planning ensures cross-ventilation, corner light and quiet corridors.",
                },
                {
                    title: "Concierge living",
                    text: "A residential concierge concept covering arrivals, reservations and everyday arrangements.",
                },
            ],
        },
        {
            type: "gallery",
            overline: "Gallery",
            title: "A study in dusk, stone and water.",
            caption: "Imagery shown is illustrative, curated to convey mood and craft.",
            images: [
                { src: IMG.interior_penthouse, caption: "Penthouse great room", span: 8 },
                { src: IMG.pool_deck, caption: "Evening pool deck", span: 4 },
                { src: IMG.interior_living_room, caption: "Residence living space", span: 4 },
                { src: IMG.entrance_lobby, caption: "Arrival lobby", span: 4 },
                { src: IMG.interior_bedroom, caption: "Primary suite", span: 4 },
                { src: IMG.balcony_dusk, caption: "Sky deck at dusk", span: 8 },
                { src: IMG.interior_kitchen, caption: "Show kitchen", span: 4 },
                { src: IMG.clubhouse_spa, caption: "Spa & wellness", span: 4 },
                { src: IMG.landscape_resort, caption: "Courtyard gardens", span: 4 },
            ],
        },
        {
            type: "configurations",
            overline: "Residences",
            title: "Three ways to live above the sea.",
            note: "Layouts and areas are illustrative concept studies — not to scale.",
            units: [
                {
                    id: "sky-villa",
                    name: "3 BHK Sky Villa",
                    area: "≈ 2,450 sq.ft. (illustrative)",
                    desc: "A full-width living and dining salon anchored by a sea-facing sky deck, with a private primary wing.",
                    features: [
                        "Private lift lobby",
                        "West-facing sky deck",
                        "Primary suite with walk-in wardrobe",
                        "Separate utility and staff access",
                    ],
                    planId: "sky-villa",
                },
                {
                    id: "penthouse",
                    name: "4 BHK Penthouse",
                    area: "≈ 4,100 sq.ft. (illustrative)",
                    desc: "A crown residence with a wraparound terrace, formal and informal living, and four en-suite chambers.",
                    features: [
                        "Wraparound terrace",
                        "Formal & family living",
                        "Four en-suite chambers",
                        "Dedicated lounge and study",
                    ],
                    planId: "penthouse",
                },
                {
                    id: "duplex",
                    name: "Duplex Residence",
                    area: "≈ 5,300 sq.ft. (illustrative)",
                    desc: "A two-level home organised around a double-height deck — entertaining below, retreat above.",
                    features: [
                        "Double-height sea deck",
                        "Upper-level family floor",
                        "Guest suite on lower level",
                        "Sculptural internal stair",
                    ],
                    planId: "duplex",
                },
            ],
        },
        {
            type: "masterplan",
            overline: "Master Plan",
            title: "Three towers, one continuous landscape.",
            note: "Illustrative site composition — tap a marker to explore.",
            hotspots: [
                { x: 210, y: 150, label: "Tower A", desc: "Sea-facing sky villas, two residences per floor." },
                { x: 400, y: 110, label: "Tower B", desc: "The central tower, crowned by penthouse residences." },
                { x: 590, y: 170, label: "Tower C", desc: "Duplex collection with double-height decks." },
                { x: 330, y: 300, label: "Club Aurelia", desc: "Two levels of wellness, dining and celebration." },
                { x: 480, y: 320, label: "Infinity Pool", desc: "A horizon-edge pool above the arrival court." },
                { x: 640, y: 350, label: "Central Gardens", desc: "Terraced lawns, courts and shaded walks." },
                { x: 180, y: 380, label: "Arrival Court", desc: "A covered, landscaped porte-cochère entrance." },
            ],
        },
        {
            type: "amenities",
            overline: "Amenities",
            title: "A private world, two levels above the city.",
            note: "Amenity programme shown is illustrative.",
            items: [
                { icon: "Waves", name: "Horizon Infinity Pool", desc: "A still edge of water against the sea." },
                { icon: "Sparkles", name: "Spa & Hammam", desc: "Treatment suites, steam and thermal rooms." },
                { icon: "Dumbbell", name: "Fitness Atelier", desc: "Strength, cardio and private training studios." },
                { icon: "Wine", name: "Wine & Tasting Room", desc: "A temperature-controlled cellar and salon." },
                { icon: "Film", name: "Private Theatre", desc: "A twelve-seat screening room." },
                { icon: "BookOpen", name: "Library & Work Salon", desc: "Quiet rooms for reading and remote work." },
                { icon: "Trees", name: "Terraced Gardens", desc: "Lawns, courts and shaded pavilions." },
                { icon: "BellRing", name: "Residential Concierge", desc: "Arrivals, reservations and everyday ease." },
            ],
        },
        {
            type: "specs",
            overline: "Specifications",
            title: "Material honesty, quietly specified.",
            note: "Specifications are illustrative and shown for presentation purposes only.",
            groups: [
                {
                    title: "Living Spaces",
                    items: [
                        "Engineered oak flooring in living and dining areas",
                        "Full-height glazing with acoustic laminated glass",
                        "Provision for integrated home automation",
                        "Concealed, zoned climate control",
                    ],
                },
                {
                    title: "Kitchens",
                    items: [
                        "European-style modular cabinetry",
                        "Quartz worktops with full-height splashback",
                        "Provision for built-in appliances",
                        "Separate utility and prep area",
                    ],
                },
                {
                    title: "Bathrooms",
                    items: [
                        "Natural stone or large-format porcelain finishes",
                        "Walk-in rain showers",
                        "Wall-hung sanitaryware with concealed cisterns",
                        "Heated towel rails in primary suites",
                    ],
                },
                {
                    title: "Building",
                    items: [
                        "RCC framed structure (concept)",
                        "High-speed destination-controlled elevators",
                        "Multi-tier access control and CCTV coverage",
                        "Full power backup for residences and commons",
                    ],
                },
            ],
        },
        {
            type: "developer",
            overline: "The Developer",
            title: "Aurelia Design Studio",
            fictional: true,
            tagline: "A fictional concept developer, created for this demonstration.",
            story: [
                "Aurelia Design Studio is not a real company. It exists to show how Sthaan presents a developer's identity, story and portfolio with the same care as the project itself.",
                "On a live Sthaan showcase, this section carries the developer's genuine profile — their history, philosophy, completed and ongoing work — using only material they have supplied and approved.",
            ],
            portfolio: [
                "Concept Tower · Alibaug",
                "Concept Residences · Lodha-style study",
                "Concept Villas · Goa",
                "Concept Workspace · BKC",
            ],
        },
        {
            type: "location",
            overline: "Location",
            title: "Worli Sea Face, Mumbai — a concept setting.",
            image: IMG.balcony_dusk,
            intro:
                "The Aurelia is imagined on Mumbai's most storied seafront. On real showcases, this section carries verified location material supplied by the developer.",
            landmarks: [
                { name: "Bandra–Worli Sea Link", detail: "Minutes away*" },
                { name: "Worli Sea Face promenade", detail: "At the doorstep*" },
                { name: "Lower Parel business district", detail: "Short drive*" },
                { name: "Elite schools & hospitals", detail: "Nearby*" },
            ],
            note: "*All distances and settings are illustrative.",
        },
        {
            type: "faqs",
            overline: "Questions",
            title: "Asked, and answered.",
            items: [
                {
                    q: "Is The Aurelia a real project?",
                    a: "No. The Aurelia is a fictional concept project created by Sthaan to demonstrate the depth and polish of a Sthaan showcase. Nothing on this page represents a real property, developer, price or approval.",
                },
                {
                    q: "Where would real project information come from?",
                    a: "Every real Sthaan page is built from material supplied and approved by the respective developer — photographs, plans, RERA details, configurations, pricing and possession timelines. Sections are only shown when genuine material exists.",
                },
                {
                    q: "How do buyers contact a developer?",
                    a: "Through the direct WhatsApp button on each showcase. The message opens in the buyer's own WhatsApp, pre-filled with the project name, and goes straight to the developer. No accounts, no brokers, no brokerage.",
                },
                {
                    q: "Does Sthaan charge buyers anything?",
                    a: "No. Sthaan never charges buyers brokerage or fees, and does not process payments or handle transactions of any kind.",
                },
                {
                    q: "What should buyers verify independently?",
                    a: "Pricing, availability, specifications, approvals, RERA registration, possession timelines and all other project details should always be verified directly with the developer before any decision.",
                },
            ],
        },
        {
            type: "cta",
            overline: "Connect Directly",
            title: "Experience the conversation, not the callback queue.",
            text: "On a live showcase this button connects you straight to the developer's own WhatsApp. Here, it demonstrates the exact flow a buyer would use.",
            showBrochure: true,
        },
    ],
};

export default aurelia;
